import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import NotificationSnackbar from "./NotificationBar";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const AddNewSourceOfFunds = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Reset and load data whenever modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
      });
      setError("");
    }
  }, [open]);

  // Source of Fund Name Change
  const handleNameChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      name: event.target.value,
    }));

    if (error) {
      setError("");
    }
  };

  // Form Validation
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Source of Fund name is required.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        name: formData.name.trim(),
      };

      const response = await fetch("/api/support/add-source-of-fund", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Failed to add source of fund. Status: ${response.status}`,
        );
      }

      console.log("Source of fund added:", result);
      showSnackbar(
        result?.message || "Source of Fund added successfully.",
        "success",
      );

      if (onSuccess) {
        onSuccess(result);
      }

      handleClose();
    } catch (err) {
      console.error("Error adding source of fund:", err);
      setError(err.message || "Failed to add source of fund.");
      showSnackbar(err.message || "Failed to add source of fund.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;

    setFormData({
      name: "",
    });
    setError("");

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
        },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          py: 2,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha("#fff", 0.15),
              }}
            >
              <AccountBalanceIcon />
            </Box>

            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Add New Source of Fund
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  opacity: 0.8,
                }}
              >
                Create a new source of fund
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: alpha("#fff", 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent
        sx={{
          p: 3,
          pt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* SOURCE OF FUND NAME */}
          <TextField
            sx={{
              marginTop: 2,
            }}
            fullWidth
            label="Source of Fund Name"
            placeholder="Enter source of fund name"
            value={formData.name}
            onChange={handleNameChange}
            required
            disabled={saving}
            autoFocus
            error={Boolean(error && !formData.name.trim())}
            helperText={
              error && !formData.name.trim()
                ? error
                : "Enter the name of the source of fund"
            }
          />
        </Box>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.015),
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            px: 2.5,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={saving || !formData.name.trim()}
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            borderRadius: "8px",
            px: 2.5,
          }}
        >
          {saving ? "Saving..." : "Save Source of Fund"}
        </Button>
      </DialogActions>

      {/* SNACKBAR */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
};

export default AddNewSourceOfFunds;
